import { IProps } from 'react-qrcode-logo';

import { HomeProfileSectionTypes, PageContentTypes, ThemeTypes } from '../../types';

export enum PROJECT_ITEM_TYPE {
  BUILDING = 'BUILDING',
  VILLA_BLOCK = 'VILLA_BLOCK',
  AMENITY = 'AMENITY',
}

export enum ORIENTATION_ANGLE {
  PORTRAIT = 'PORTRAIT',
  LANDSCAPE = 'LANDSCAPE',
}

export enum AREA_SHAPE {
  POLY = 'poly',
  RECT = 'rect',
  CIRCLE = 'circle',
}

export enum LOCATION_GEO_JSON_TYPES {
  POINT = 'Point',
  LINE_STRING = 'LineString',
  POLYGON = 'Polygon',
  MULTI_POINT = 'MultiPoint',
  MULTI_LINE_STRING = 'MultiLineString',
  MULTI_POLYGON = 'MultiPolygon',
}

export enum SHARE_LINK_KEY {
  // common
  CONTENT_VIEW = 'v',
  PROJECT_ITEM = 'pi',
  PROJECT_CHILD_ITEM = 'pci',
  // site-plan
  BUILDING = 'bu',
  BLOCK = 'bl',
  FLOOR = 'fl',
  APARTMENT = 'ap',
  VILLA = 'va',
  // amenity
  AMENITY = 'am',
  FACILITY_GROUP = 'fg',
  FACILITY_FOCUS_ITEM = 'ff',
  FACILITY = 'fa',
  // location
  LOCATION_GROUP = 'lg',
  LOCATION_SUB_GROUP = 'sg',
  LOCATION_ITEM = 'li',
  LOCATION_FOCUS_ITEM = 'lf',
  // about
  ABOUT_ITEM = 'ai',
  COOPERATOR_DETAIL = 'cd',
}

export enum BREADCRUMB_BACKGROUND_MODE {
  DEFAULT = 'DEFAULT',
  LIGHT = 'LIGHT',
}

export enum DESCRIPTION_ACTION_MODE {
  INLINE = 'INLINE',
  SHAPE = 'SHAPE',
}

export enum PROJECT_CONFIG_KEY {
  LOADING_LOGO = 'LOADING_LOGO',
}

export enum FILE_TYPE_EXTENSION {
  SVG = 'svg',
  PNG = 'png',
  GIF = 'gif',
}

export enum SLIDER_MAPPING_TYPE {
  SITE_PLAN = 'SITE_PLAN',
  NORMAL_SLIDER = 'NORMAL_SLIDER',
  IMAGE_SLIDER = 'IMAGE_SLIDER',
  FORM_SLIDER = 'FORM_SLIDER',
  FLOOR_SLIDER = 'FLOOR_SLIDER',
}

export enum CLICK_SLIDER_ITEM_TYPE {
  VIEW_360 = 'VIEW_360',
  MODE_3D = 'MODE_3D',
  DETAIL = 'DETAIL',
  GALLERY = 'GALLERY',
  SUBMIT = 'SUBMIT',
  PAUSE = 'PAUSE',
  GALLERY_VIEW_360 = 'GALLERY_VIEW_360',
}

export enum PROMOTION_ELEMENT_ID {
  TOP = 'bds-promotion__top',
  BOTTOM = 'bds-promotion__bottom',
}

export enum ANALYZER_ACTION {
  VIEWED = 'VIEWED',
  CLOSED = 'CLOSED',
  RESUMED = 'RESUMED',
  INTERRUPTED = 'INTERRUPTED',
  LOADED = 'LOADED',
  UN_LOADED = 'UN_LOADED',
}

export enum ANALYZER_ENTITY {
  PROJECT = 'PROJECT',
  BUILDING = 'BUILDING',
  OVERVIEW_SITEPLAN = 'OVERVIEW SITEPLAN',
  FLOOR_PLAN = 'FLOOR PLAN',
  APARTMENT = 'APARTMENT',
  HOME_PROFILE = 'HOME PROFILE',
  AMENITY = 'AMENITY',
  FACILITY = 'FACILITY',
  LOCATION_GROUP = 'LOCATION GROUP',
  LOCATION_SUBGROUP = 'LOCATION SUBGROUP',
  LOCATION = 'LOCATION',
  INFORMATION_MODULE = 'INFORMATION_MODULE',
  AMENITY_MODULE = 'AMENITY_MODULE',
  LOCATION_MODULE = 'LOCATION_MODULE',
  SITEPLAN_MODULE = 'SITEPLAN_MODULE',
}

export enum BOTTOM_SHEET_SWIPE_LEVEL {
  MAX = 'MAX',
  MIDDLE = 'MIDDLE',
  MIN = 'MIN',
}

export enum SELF_HOME_PROFILE_LANGUAGE_PATH {
  EN = 'homeprofile',
  VI = 'can-ho',
  JP = 'homeprofile-jp',
}

export enum ABOUT_LANGUAGE_PATH {
  EN = 'overview',
  JP = 'overview-jp',
  VI = 'thong-tin',
}

export enum LOCATION_LANGUAGE_PATH {
  EN = 'locations',
  JP = 'locations-jp',
  VI = 'vi-tri',
}

export enum AMENITIES_LANGUAGE_PATH {
  EN = 'facilities',
  JP = 'facilities-jp',
  VI = 'tien-ich',
}

export enum SITEPLAN_LANGUAGE_PATH {
  EN = 'layout',
  JP = 'layout-jp',
  VI = 'mat-bang',
}

export enum NEWS_LANGUAGE_PATH {
  EN = 'news',
  JP = 'news-jp',
  VI = 'tin-tuc',
}

export const CONTENT_KEY: { [key: string]: PageContentTypes } = {
  ABOUT: {
    key: 'ABOUT',
    metaTagTitle: 'common.content-key.meta-tag.title.about',
    icon: 'icon-residential_24x24',
    menuTitle: 'common.content-key.menu.title.about',
    configKey: 'REVIEW',
    configTitle: 'about.additional-information.title',
    analyzerEntity: ANALYZER_ENTITY.INFORMATION_MODULE,
  },
  LOCATION: {
    key: 'LOCATION',
    metaTagTitle: 'common.content-key.meta-tag.title.location',
    icon: 'icon-place_24x24',
    menuTitle: 'common.content-key.menu.title.location',
    configKey: 'LOCATION',
    configTitle: 'location.additional-information.title',
    analyzerEntity: ANALYZER_ENTITY.LOCATION_MODULE,
  },
  AMENITIES: {
    key: 'AMENITIES',
    metaTagTitle: 'common.content-key.meta-tag.title.amenities',
    icon: 'icon-local_play_24x24',
    menuTitle: 'common.content-key.menu.title.amenities',
    configKey: 'AMENITIES',
    configTitle: 'amenities.overview-name',
    analyzerEntity: ANALYZER_ENTITY.AMENITY_MODULE,
  },
  SITEPLAN: {
    key: 'SITEPLAN',
    metaTagTitle: 'common.content-key.meta-tag.title.site-plan',
    icon: 'icon-floor-plan_24x24',
    menuTitle: 'common.content-key.menu.title.site-plan',
    configKey: 'SITEPLAN',
    configTitle: 'site-plan.additional-information.title',
    analyzerEntity: ANALYZER_ENTITY.SITEPLAN_MODULE,
  },
  NEWS: {
    key: 'NEWS',
    metaTagTitle: 'common.content-key.meta-tag.title.news',
    icon: '',
  },
  HOME_PROFILE: {
    key: 'HOME_PROFILE',
    icon: '',
    metaTagTitle: 'common.content-key.meta-tag.title.home-profile',
  },
  HOME_PROFILE_SHOWROOM: {
    key: 'HOME_PROFILE_SHOWROOM',
    icon: '',
    metaTagTitle: 'common.content-key.meta-tag.title.home-profile',
    param: 'showroom',
  },
  HOME_PROFILE_PROMOTE: {
    key: 'HOME_PROFILE_PROMOTE',
    icon: '',
    metaTagTitle: 'common.content-key.meta-tag.title.home-profile',
    param: 'promote',
  },
};

export const CONTENT_VISUALIZATION_MODE = {
  MODE_2D: {
    key: 'MODE_2D',
    icon: 'icon-mode_2D_24x24',
  },
  MODE_DEG_360: {
    key: 'MODE_DEG_360',
    icon: 'icon-deg_360_24x24',
  },
  MODE_3D: {
    key: 'MODE_3D',
    icon: 'icon-mode_3D_24x24',
  },
  MODE_VIEW_ON_MAP: {
    key: 'VIEW_ON_MAP',
    name: 'On Map',
  },
  MODE_VIEW_ON_LOCAL_MAP: {
    key: 'VIEW_ON_LOCAL_MAP',
    name: 'On Local Map',
  },
  MODE_HIDE_ON_MAP: {
    key: 'HIDE_ON_MAP',
    name: 'Hide Map',
  },
  MODE_HIDE_ON_LOCAL_MAP: {
    key: 'HIDE_ON_LOCAL_MAP',
    name: 'Hide Map',
  },
};

export const DIRECTION_TYPE = {
  EAST: {
    key: 'EAST',
    name: 'common.direction.east',
  },
  WEST: {
    key: 'WEST',
    name: 'common.direction.west',
  },
  SOUTH: {
    key: 'SOUTH',
    name: 'common.direction.south',
  },
  NORTH: {
    key: 'NORTH',
    name: 'common.direction.north',
  },
  SOUTHEAST: {
    key: 'SOUTHEAST',
    name: 'common.direction.south-east',
  },
  SOUTHWEST: {
    key: 'SOUTHWEST',
    name: 'common.direction.south-west',
  },
  NORTHEAST: {
    key: 'NORTHEAST',
    name: 'common.direction.north-east',
  },
  NORTHWEST: {
    key: 'NORTHWEST',
    name: 'common.direction.north-west',
  },
};

export const BEDROOM_TYPE = {
  ONE: {
    key: 'ONE',
    count: 1,
    name: 'common.bedroom-type.one',
  },
  TWO: {
    key: 'TWO',
    count: 2,
    name: 'common.bedroom-type.two',
  },
  THREE: {
    key: 'THREE',
    count: 3,
    name: 'common.bedroom-type.three',
  },
  DUAL_KEY: {
    key: 'DUAL_KEY',
    count: 0,
    name: 'common.bedroom-type.dual-key',
  },
  PENT_HOUSE: {
    key: 'PENT_HOUSE',
    count: 0,
    name: 'common.bedroom-type.pent-house',
  },
};

export const SHORT_INFORMATION = {
  NUMBER_OF_FLOORS: {
    key: 'NUMBER_OF_FLOORS',
    name: 'common.short-information.number-of-floor',
  },
  NUMBER_OF_APARTMENTS: {
    key: 'NUMBER_OF_APARTMENTS',
    name: 'common.short-information.number-of-apartment',
  },
  YEAR_OF_TAKING_OVER: {
    key: 'YEAR_OF_TAKING_OVER',
    name: 'common.short-information.year-of-taking-over',
  },
  START_DATE: {
    key: 'START_DATE',
    name: 'common.short-information.start-date',
  },
  STATUS: {
    key: 'STATUS',
    name: 'common.short-information.status',
  },
  TYPE: {
    key: 'TYPE',
    name: 'common.short-information.type',
  },
  PRICE: {
    key: 'PRICE',
    name: 'common.short-information.price',
  },
};

export const PROJECT_CHILD_ITEM_SHORT_INFORMATION = {
  bathroom: {
    key: 'bathroom',
    name: 'site-plan.modal-detail.bathroom',
  },
  bedroom: {
    key: 'bedroom',
    name: 'site-plan.modal-detail.bedroom',
  },
  direction: {
    key: 'direction',
    name: 'site-plan.modal-detail.direction',
  },
  balconyDirections: {
    key: 'balconyDirections',
    name: 'site-plan.modal-detail.balcony-direction',
  },
  square: {
    key: 'square',
    name: 'site-plan.modal-detail.square',
  },
  usableSquare: {
    key: 'usableSquare',
    name: 'site-plan.modal-detail.usable-square',
  },
};

export const PROJECT_CHILD_ITEM_TYPE = {
  APARTMENT: {
    key: 'APARTMENT',
    name: 'site-plan.child-item.apartment',
  },
  VILLA: {
    key: 'VILLA',
    name: 'site-plan.child-item.villa',
  },
};

export const SITE_PLAN_SHORTCUT_MODE = {
  CHILD_ITEM_LISTING: {
    key: 'CHILD_ITEM_LISTING',
  },
  PROJECT_ITEM_INFO: {
    key: 'PROJECT_ITEM_INFO',
    label: 'site-plan.shortcut.information',
  },
  FILTER_FLOOR: {
    key: 'FILTER_FLOOR',
    label: 'site-plan.shortcut.filter-floor',
  },
  SEARCH_CHILD_ITEM: {
    key: 'SEARCH_CHILD_ITEM',
    label: 'site-plan.shortcut.search-child-item',
  },
  FILTER_CHILD_ITEM: {
    key: 'FILTER_CHILD_ITEM',
    label: 'site-plan.shortcut.filter-child-item',
  },
};

export const PROJECT_CHILD_ITEM_POSITION_TYPE = {
  SIDE: {
    key: 'SIDE',
    label: 'site-plan.child-item.position.side',
  },
  MIDDLE: {
    key: 'MIDDLE',
    label: 'site-plan.child-item.position.middle',
  },
};

export const PROJECT_CHILD_ITEM_FURNITURE_STATUS = {
  NONE: {
    key: 'NONE',
    name: 'common.furniture-status.none',
  },
  BASIC: {
    key: 'BASIC',
    name: 'common.furniture-status.basic',
  },
  FULL: {
    key: 'FULL',
    name: 'common.furniture-status.full',
  },
};

export const PROJECT_CHILD_ITEM_RESIDENCE_TYPE = {
  APARTMENT: {
    key: 'APARTMENT',
    name: 'common.residence-type.apartment',
  },
  FLAT: {
    key: 'FLAT',
    name: 'common.residence-type.flat',
  },
  STUDIO: {
    key: 'STUDIO',
    name: 'common.residence-type.studio',
  },
  PENTHOUSE: {
    key: 'PENTHOUSE',
    name: 'common.residence-type.penthouse',
  },
  CONDOMINIUM: {
    key: 'CONDOMINIUM',
    name: 'common.residence-type.condominium',
  },
  CONDOTEL: {
    key: 'CONDOTEL',
    name: 'common.residence-type.condotel',
  },
  DUPLEX: {
    key: 'DUPLEX',
    name: 'common.residence-type.duplex',
  },
  BUNGALOW: {
    key: 'BUNGALOW',
    name: 'common.residence-type.bungalow',
  },
  TWIN: {
    key: 'TWIN',
    name: 'common.residence-type.twin',
  },
  SEMI_DETACHED: {
    key: 'SEMI_DETACHED',
    name: 'common.residence-type.semi-detached',
  },
  DETACHED: {
    key: 'DETACHED',
    name: 'common.residence-type.detached',
  },
  COASTAL: {
    key: 'COASTAL',
    name: 'common.residence-type.coastal',
  },
  MANSION: {
    key: 'MANSION',
    name: 'common.residence-type.mansion',
  },
  TOWNHOUSE: {
    key: 'TOWNHOUSE',
    name: 'common.residence-type.townhouse',
  },
  GARDEN_TOWNHOUSE: {
    key: 'GARDEN_TOWNHOUSE',
    name: 'common.residence-type.garden-townhouse',
  },
  TERRACED_HOUSE: {
    key: 'TERRACED_HOUSE',
    name: 'common.residence-type.terraced-house',
  },
  SHOP_HOUSE: {
    key: 'SHOP_HOUSE',
    name: 'common.residence-type.shop-house',
  },
};

export const PROJECT_CHILD_ITEM_LAND_USE_RIGHT = {
  PINK_BOOK: {
    key: 'PINK_BOOK',
    name: 'common.land-use-right.pink-book',
  },
  RED_BOOK: {
    key: 'RED_BOOK',
    name: 'common.land-use-right.red-book',
  },
  CONTRACT: {
    key: 'CONTRACT',
    name: 'common.land-use-right.contract',
  },
  HOUSE_OWNERSHIP_CERTIFICATE: {
    key: 'HOUSE_OWNERSHIP_CERTIFICATE',
    name: 'common.land-use-right.house-ownership-certificate',
  },
};

export const PROJECT_CHILD_ITEM_SELL_STATUS = {
  NOT_SOLD_YET: {
    key: 'NOT_SOLD_YET',
    name: 'common.sell-status.not-sold-yet',
  },
  SOLD: {
    key: 'SOLD',
    name: 'common.sell-status.sold',
  },
  RENTED: {
    key: 'RENTED',
    name: 'common.sell-status.rented',
  },
  ON_SALE: {
    key: 'ON_SALE',
    name: 'common.sell-status.on-sale',
  },
  INACTIVE: {
    key: 'INACTIVE',
    name: 'common.sell-status.inactive',
  },
  RESERVED: {
    key: 'RESERVED',
    name: 'common.sell-status.reserved',
  },
  DEPOSITED: {
    key: 'DEPOSITED',
    name: 'common.sell-status.deposited',
  },
  OTHER: {
    key: 'OTHER',
    name: 'common.sell-status.other',
  },
  FOR_RENT: {
    key: 'FOR_RENT',
    name: 'common.sell-status.for-rent',
  },
};

export const HOME_PROFILE_FEATURE_ACTIONS = {
  LAYOUT: {
    key: 'LAYOUT',
    label: 'home-profile.feature-action.layout',
    icon: 'icon-home_assets_layout_24x24',
    variable: '',
  },
  VIEW_360: {
    key: 'VIEW_360',
    label: 'home-profile.feature-action.view-360',
    icon: 'icon-deg_360_24x24',
    variable: 'virtualTourUrl',
    variableId: 1,
  },
  HOME_FURNITURE: {
    key: 'HOME_FURNITURE',
    label: 'home-profile.feature-action.home-furniture',
    icon: 'icon-home_assets_sofa_single_24x24',
    variable: 'furnitureUrl',
    variableId: 2,
  },
  LOCATION: {
    key: 'LOCATION',
    label: 'home-profile.feature-action.location',
    icon: 'icon-location_outline_24x24',
    variable: '',
  },
  GALLERY: {
    key: 'GALLERY',
    label: 'home-profile.feature-action.gallery',
    icon: 'icon-image_alt_24x24',
    variable: '',
  },
  FINANCE: {
    key: 'FINANCE',
    label: 'home-profile.feature-action.finance',
    icon: 'icon-home_finance_24x24',
    variable: 'financeUrl',
  },
};

export const HOME_PROFILE_CONTENT = {
  DETAIL_INFO: {
    key: 'DETAIL_INFO',
    title: 'home-profile.content.detail-info.title',
    icon: 'icon-info_24x24',
  },
  FACILITY: {
    key: 'FACILITY',
    title: 'home-profile.content.facility.title',
    icon: '',
  },
  MOBILE_RATING: {
    key: 'MOBILE_RATING',
    title: '',
    icon: '',
  },
  // Asked Questions
  ASKED_QUESTIONS: {
    key: 'ASKED_QUESTIONS',
    title: 'home-profile.content.facility.title',
    icon: '',
  },
};

export const LOCATION_TRANSPORT = {
  DRIVING: {
    key: 'DRIVING',
    variable: 'driving',
  },
  TRANSIT: {
    key: 'TRANSIT',
    variable: 'transit',
  },
  WALKING: {
    key: 'WALKING',
    variable: 'walking',
  },
};

export const ABOUT_ITEMS = {
  news: {
    key: 'news',
    title: 'about.news',
    subTitle: 'about.news.description',
  },
  introduction: {
    key: 'introduction',
    title: 'about.general-introduction',
    subTitle: 'about.general-introduction.description',
  },
  cooperators: {
    key: 'cooperators',
    title: 'about.cooperators',
    subTitle: 'about.cooperators.description',
  },
  cooperatorsDetail: {
    key: 'cooperatorsDetail',
    title: 'about.cooperatorsDetail',
    subTitle: 'about.cooperatorsDetail.description',
  },
  gallery: {
    key: 'gallery',
    title: 'about.gallery',
    subTitle: 'about.gallery.description',
  },
};

export const GALLERY_TYPE = {
  IMAGE: {
    key: 'IMAGE',
    title: 'IMAGE',
  },
  VIDEO: {
    key: 'VIDEO',
    title: 'VIDEO',
  },
  VT360: {
    key: 'VT360',
    title: 'VT360',
  },
  DOCUMENT: {
    key: 'DOCUMENT',
    title: 'DOCUMENT',
  },
  YOUTUBE: {
    key: 'YOUTUBE',
    title: 'YOUTUBE',
  },
};

export const FACILITY_FEATURE_ACTIONS = {
  VIEW_360: {
    key: 'VIEW_360',
    label: 'amenities.facility-detail.visit',
    icon: 'icon-deg_360_24x24',
    variable: 'virtualTourUrl',
  },
  GALLERY: {
    key: 'GALLERY',
    label: 'amenities.feature-action.gallery',
    icon: 'icon-image_alt_24x24',
    variable: '',
  },
};

export const TOOLTIP_STYLES = {
  color: '#000',
  background: '#fff',
};

export const NONE_GROUP_FACILITY_IN_AMENITY = {
  key: 'facility-group',
  label: 'amenities.facility.name',
};

export const BUTTON_GROUP_ABOUT_DETAIL = {
  phone: {
    key: 'phone',
    icon: 'icon-phone_24x24',
  },
  web: {
    key: 'web',
    icon: 'icon-website_24x24',
  },
  zalo: {
    key: 'zalo',
    icon: 'icon-zalo_24x24',
  },
  gmail: {
    key: 'gmail',
    icon: 'icon-mail_24x24',
  },
  facebook: {
    key: 'facebook',
    icon: 'icon-facebook_24x24',
  },
};

export const FORM_BANNER = {
  FORM_EMAIL_AND_PHONE: {
    key: 'FORM_1',
    label: '',
  },
  FORM_EMAIL_OR_PHONE: {
    key: 'FORM_2',
    label: '',
  },
};

export const HOME_PROFILE_PROMOTION = {
  HOME_PROFILE_PROMOTION: {
    key: 'HOME_PROFILE_PROMOTION',
    nameArray: 'homeProfilePromotion',
  },

  HOME_PROFILE_BUTTON: {
    key: 'HOME_PROFILE_BUTTON',
    nameArray: 'homeProfileButtons',
  },
  HOME_PROFILE_BUTTON_BANNER: {
    key: 'HOME_PROFILE_BUTTON_BANNER',
    nameArray: 'homeProfileButtonBanners',
  },
  HOME_PROFILE_MOBILE_BOTTOM: {
    key: 'HOME_PROFILE_MOBILE_BOTTOM',
    nameArray: 'homeProfileMobileBottoms',
  },
  HOME_PROFILE_MOBILE_TOP: {
    key: 'HOME_PROFILE_MOBILE_TOP',
    nameArray: 'homeProfileMobileTops',
  },
  HOME_PROFILE_POPUP: {
    key: 'HOME_PROFILE_POPUP',
    nameArray: 'homeProfilePopups',
  },
  HOME_PROFILE_TABLET_BOTTOM: {
    key: 'HOME_PROFILE_TABLET_BOTTOM',
    nameArray: 'homeProfileTabletBottoms',
  },
  HOME_PROFILE_TABLET_TOP: {
    key: 'HOME_PROFILE_TABLET_TOP',
    nameArray: 'homeProfileTabletTops',
  },
  HOME_PROFILE_DESKTOP_TOP: {
    key: 'HOME_PROFILE_DESKTOP_TOP',
    nameArray: 'homeProfileDesktopTops',
  },
  HOME_PROFILE_DESKTOP_BOTTOM: {
    key: 'HOME_PROFILE_DESKTOP_BOTTOM',
    nameArray: 'homeProfileDesktopBottoms',
  },
  HOME_PROFILE_RATING_BANNER: {
    key: 'HOME_PROFILE_RATING_BANNER',
    nameArray: 'homeProfileRatingBanner',
  },
  HOME_PROFILE_INFORMATION_BANNER: {
    key: 'HOME_PROFILE_INFORMATION_BANNER',
    nameArray: 'homeProfileInformationBanner',
  },
  HOME_PROFILE_FURNISHING_APARTMENT_BANNER: {
    key: 'HOME_PROFILE_FURNISHING_APARTMENT_BANNER',
    nameArray: 'homeProfileFurnishingApartmentBanner',
  },
  HOME_PROFILE_PROJECT_INTRO_BANNER: {
    key: 'HOME_PROFILE_PROJECT_INTRO_BANNER',
    nameArray: 'homeProfileProjectIntroBanner',
  },
  HOME_PROFILE_PROJECT_LOCATION_BANNER: {
    key: 'HOME_PROFILE_PROJECT_LOCATION_BANNER',
    nameArray: 'homeProfileProjectLocationBanner',
  },
  HOME_PROFILE_LOCAL_AMENITIES_BANNER: {
    key: 'HOME_PROFILE_LOCAL_AMENITIES_BANNER',
    nameArray: 'homeProfileLocalAmenitiesBanner',
  },
};

export const THEME_TYPE: { [key: string]: ThemeTypes } = {
  DARK: { key: 'DARK', dataTheme: 'dark' },
  LIGHT: { key: 'LIGHT', dataTheme: 'light' },
};

export const HOME_PROFILE_VIEW_SECTION: { [key: string]: HomeProfileSectionTypes } = {
  REVIEW: {
    key: 'REVIEW',
    elementId: 'home-profile__review-section',
    icon: 'icon-info_24x24',
    label: 'home-profile.section.review',
  },
  FINANCE: {
    key: 'FINANCE',
    elementId: 'home-profile__finance-section',
    icon: 'icon-money_24x24',
    label: 'home-profile.section.finance',
  },
  SHOWROOM: {
    key: 'SHOWROOM',
    elementId: 'home-profile__showroom-section',
    icon: 'icon-cloud_down_24x24',
    label: 'home-profile.section.showroom',
  },
  PROMOTION: {
    key: 'PROMOTION',
    elementId: 'home-profile__promotion-section',
    icon: 'icon-action_redeem_24x24',
    label: 'home-profile.section.promotion',
  },
};

export const NODE_ENV_TYPES = {
  dev: { key: 'dev', label: 'DEV' },
  ci: { key: 'ci', label: 'CI' },
  stag: { key: 'stag', label: 'STAG' },
  preview: { key: 'preview', label: 'PREVIEW' },
};

export enum TABS_SPAN_INDEX {
  overview = 0,
  reviews = 1,
  detail = 2,
  promotion = 3,
  facility = 4,
  location = 5,
  amenities = 6,
  similar = 7,
  questions = 8,
}

export const SECTION_OFFSET = 64;

export const DEFAULT_TABS_MODEL = [
  {
    id: 'section-overview',
    label: 'home-profile.section.overview',
  },
  {
    id: 'section-reviews',
    label: 'home-profile.rating-view.title',
  },
  {
    id: 'section-detail',
    label: 'home-profile.content.detail-info.title',
  },
  {
    id: 'section-promotion',
    label: 'home-profile.content.promotion.title',
  },
  {
    id: 'section-facility',
    label: 'home-profile.content.facility.title',
  },
  {
    id: 'section-location',
    label: 'location.additional-information.title',
  },
  {
    id: 'section-amenities',
    label: 'amenities.overview-name',
  },
  {
    id: 'section-similar',
    label: 'home-profile.similar-apartment.title',
  },
  {
    id: 'section-questions',
    label: 'home-profile.asked-questions.title',
  },
];

export const EMPTY_ADDITIONAL_INFO = {
  key: '',
  title: '',
  subTitle: '',
  image: null,
};

export enum PANEL_LINE_HEIGHT {
  auto = 'auto',
}

export const DEFAULT_SETTING_QRCODE: IProps = {
  ecLevel: 'Q',
  size: 240,
  quietZone: 16,
  fgColor: '#242840',
  logoOpacity: 0.8,
  eyeRadius: { outer: 100, inner: 10 },
};

export const AI_QUESTION_TAGS = {
  ABOUT: 'project, review',
  LOCATION: 'project, location',
  AMENITIES: 'project, amenities',
  SITEPLAN: 'project, siteplan',
  HOME_PROFILE: 'project, homeprofile',
  SITEPLAN_DETAIL: 'project, building_block',
};

export enum QUESTION_TYPE {
  FAQ = 'FAQ',
  AI_QUESTION = 'AI_QUESTION',
}
